// The key grammar of the curated-exercise half of the `$text/` keyed-text store
// in `global_common` (#4210), plus the rule that resolves an exercise's display
// text out of it (#4285).
//
// This lives in `shared/` rather than in the client because THREE runtimes need
// the same grammar and the same resolution rule: the app (`text-locale.ts`
// re-exports everything here), the server's workout-plan-scanner catalog, and
// the `tools/` GDPR export. Before #4285 only the app read this store, so the
// grammar sat in the client alone; a second and third copy of a document-id
// parser would drift apart silently, and nothing would report the drift.
//
// Nothing here touches Angular, PouchDB or the client's generated locale types,
// so it runs under plain node. A locale segment is taken as a plain string; the
// client keeps its own typed wrappers in `text-locale.ts` so that a segment
// which is not a real segment stays a compile error there.
//
// See design-docs/global-common-localization.md.

import { isCuratedExerciseById } from './exercise';

/**
 * The locale segment English documents have always used. It doubles as the
 * fallback segment: any document without a sibling in the active language is
 * served from here, so the app never renders blank text.
 */
export const ENGLISH_TEXT_SEGMENT = 'en-us';

const EXERCISE_KEY_PREFIX = '@exercise:';

/**
 * The curated-exercise fields that live in the `$text/` store, one document per
 * field. Custom (user-written) exercises have no such documents — see
 * design-docs/global-common-localization.md, "Curated vs custom".
 */
export const EXERCISE_TEXT_FIELDS = ['name', 'shortname', 'description'] as const;
export type ExerciseTextField = typeof EXERCISE_TEXT_FIELDS[number];

// matches a curated-exercise text document in ANY locale segment, capturing the
// short exercise id and the field. Built from EXERCISE_KEY_PREFIX so the literal
// cannot drift. The id part is `[^.]+` because no curated exercise id contains a
// dot — the whole scheme depends on that, which is why `exerciseTextKey()`
// refuses one that does.
const EXERCISE_DOC_ID_PATTERN = new RegExp(
  `^\\$text/[^/]+/${EXERCISE_KEY_PREFIX}([^.]+)\\.(${EXERCISE_TEXT_FIELDS.join('|')})$`);

/**
 * The short exercise id used inside a `$text/` key: the exercise document id
 * with its `exercise/` prefix removed (`exercise/$squat` -> `$squat`).
 *
 * The prefix is dropped because it contains a `/`, and a key holding one would
 * make the document id's own `$text/<segment>/<key>` structure ambiguous to
 * parse.
 */
export function shortExerciseId(exerciseDocId: string): string {
  return exerciseDocId.startsWith('exercise/') ? exerciseDocId.slice('exercise/'.length) : exerciseDocId;
}

/**
 * The keyed-text key for one field of one curated exercise, e.g.
 * `@exercise:$squat.name`. Takes the exercise DOCUMENT id (`exercise/$squat`).
 *
 * A dot in the exercise id would make the `<id>.<field>` split ambiguous, so it
 * throws rather than silently minting a key that cannot be parsed back. No
 * curated exercise id contains one today, and the English-document generator
 * asserts the same thing.
 */
export function exerciseTextKey(exerciseDocId: string, field: ExerciseTextField): string {
  const shortId = shortExerciseId(exerciseDocId);
  if (shortId.includes('.')) {
    throw Error(`Exercise id '${exerciseDocId}' contains a dot, which $text/ keys cannot represent`);
  }
  return `${EXERCISE_KEY_PREFIX}${shortId}.${field}`;
}

/**
 * Id prefix used to list every curated-exercise text document of one locale.
 *
 * The client calls this through `exerciseTextDocIdPrefixForSegment`, which
 * narrows the segment to a real one at compile time. The server and `tools/`
 * pass `ENGLISH_TEXT_SEGMENT` directly — they only ever read English.
 */
export function exerciseTextDocIdPrefix(segment: string): string {
  return `$text/${segment}/${EXERCISE_KEY_PREFIX}`;
}

/** Full document id for one field of one curated exercise, in one locale. */
export function exerciseTextDocId(
  exerciseDocId: string,
  field: ExerciseTextField,
  segment: string,
): string {
  return `$text/${segment}/${exerciseTextKey(exerciseDocId, field)}`;
}

/**
 * The short exercise id and field inside a `$text/<any-locale>/@exercise:<id>.<field>`
 * document id, or `undefined` when the id is not one. Callers iterate over
 * whatever the database returned, so one malformed document must not take down
 * the whole map.
 */
export function tryExerciseTextKeyFromDocId(
  docId: string,
): { shortId: string; field: ExerciseTextField } | undefined {
  const match = docId.match(EXERCISE_DOC_ID_PATTERN);
  return match ? { shortId: match[1], field: match[2] as ExerciseTextField } : undefined;
}

/** One locale's curated-exercise text, keyed by `<shortExerciseId>.<field>`. */
export type ExerciseTextMap = ReadonlyMap<string, string>;

/**
 * Build the lookup map for one locale from the documents an id-prefix query
 * returned.
 *
 * A document whose `text` is missing or blank is skipped, and that is a DEFINED
 * state rather than a fault: writing blank text is how the admin editor records
 * "this field is empty now" (see `decideExerciseTextWrite`), and 75 of the 157
 * curated exercises have no shortname at all. Skipping it means the field falls
 * through to the inline value, which is the same emptiness. So a blank is not
 * reported — there is nothing for a debugger to act on.
 *
 * A document whose id does not parse IS a fault, because every id here came from
 * an `@exercise:` prefix query. Those go into `malformedDocIds` for the caller
 * to report.
 */
export function buildExerciseTextMap(
  docs: readonly { _id: string; text?: string }[],
  malformedDocIds?: string[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const doc of docs) {
    const parsed = tryExerciseTextKeyFromDocId(doc._id);
    if (!parsed) {
      malformedDocIds?.push(doc._id);
      continue;
    }
    if (typeof doc.text !== 'string' || doc.text.trim() === '') {
      continue;
    }
    map.set(`${parsed.shortId}.${parsed.field}`, doc.text);
  }
  return map;
}

/** The exercise fields a `$text/` translation can replace. */
export interface LocalizableExercise {
  _id: string;
  name?: string;
  shortname?: string;
  description?: string;
}

/**
 * Fill an exercise's display text from the `$text/` store: the active language
 * first, then English, then the exercise's own inline field.
 *
 * The three-step order is what makes one code path serve everything. A CURATED
 * exercise has `$text/` documents and takes the first or second step. A CUSTOM
 * exercise — written by a user or a coach, living in the separate
 * `global_exercises` database — has none, falls through to the inline field, and
 * is therefore never translated, with no special case anywhere. And a curated
 * exercise whose translation has not been written yet shows English rather than
 * a blank, per document and per field, exactly like the help texts.
 *
 * Pass the SAME map for both arguments to resolve English only. That is what a
 * caller reading text it is about to write back wants (`ExerciseTextLanguage.Authoring`),
 * and it is also all the server and `tools/` ever need.
 *
 * ENGLISH DRIVES THE RESULT: a field with no English `$text/` entry keeps its
 * inline value even when a translation exists for it. This is the same rule
 * `mergeLocalizedWithEnglish` applies to help lists, and it is what stops a
 * stale translation from outliving its source. When an admin blanks a field, the
 * English entry goes away — and without this rule the Spanish, Dutch and French
 * readers would keep being served the removed text forever, because a
 * translation is only ever written for English text that exists.
 *
 * Returns a shallow COPY when anything changed, and the original object when
 * nothing did. The input is the cached PouchDB document, so mutating it in place
 * would poison the cache for every other reader and could write translated text
 * into the document on the next save. A curated document carries no text of its
 * own since #4285, so a curated exercise always comes back as a copy; a custom
 * one comes back as itself.
 */
export function hydrateExerciseText<T extends LocalizableExercise>(
  exercise: T,
  localized: ExerciseTextMap,
  english: ExerciseTextMap,
): T {
  const shortId = shortExerciseId(exercise._id);
  let hydrated: T | undefined;
  for (const field of EXERCISE_TEXT_FIELDS) {
    const key = `${shortId}.${field}`;
    const englishValue = english.get(key);
    // if English has nothing to say about this field, neither does any
    // translation of it — keep the inline value
    if (englishValue === undefined) {
      continue;
    }
    const value = localized.get(key) ?? englishValue;
    if (value === exercise[field]) {
      continue;
    }
    // copy on first change only — an exercise with no `$text/` documents at all
    // (every custom one) is returned untouched
    hydrated = hydrated ?? { ...exercise };
    (hydrated as LocalizableExercise)[field] = value;
  }
  return hydrated ?? exercise;
}

/**
 * The exercise as it should be STORED: a curated exercise without its
 * `name` / `shortname` / `description`, a custom one untouched.
 *
 * Since #4285 the `$text/` store is the only home of curated exercise text. The
 * admin editor holds a hydrated copy, so writing it back whole would put the
 * three fields on the document again, where nothing reads them and nothing keeps
 * them in step. A custom exercise is never in the store, so its inline text is
 * the only copy and must stay.
 *
 * Returns a copy for a curated exercise, so the caller's object keeps showing
 * the text the user just saved.
 */
export function withoutInlineExerciseText<T extends LocalizableExercise>(exercise: T): T {
  if (!isCuratedExerciseById(exercise._id)) {
    return exercise;
  }
  const stored = { ...exercise };
  for (const field of EXERCISE_TEXT_FIELDS) {
    delete (stored as LocalizableExercise)[field];
  }
  return stored;
}

/**
 * The ids of curated exercises that came out of hydration with no name.
 *
 * Since #4285 a curated exercise has no inline text to fall back to, so a blank
 * name means its English `$text/` document is missing — a fault for a debugger,
 * not a state to show the user silently. Custom exercises are not checked: they
 * never have documents here.
 *
 * Answers nothing when the English map is EMPTY. Then the whole read failed or
 * never replicated, every curated exercise would be named, and the caller has
 * already reported that one fault on its own.
 */
export function curatedExerciseIdsWithoutName(
  hydratedExercises: readonly LocalizableExercise[],
  english: ExerciseTextMap,
): string[] {
  if (english.size === 0) {
    return [];
  }
  return hydratedExercises
    .filter(exercise => isCuratedExerciseById(exercise._id))
    .filter(exercise => typeof exercise.name !== 'string' || exercise.name.trim() === '')
    .map(exercise => exercise._id);
}
